import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
// V25 — device clock/locale signals (same headers the mobile app sends) so the
// LMS country resolver treats an Indian browser clock as India.
let _clientSignals = null;
const clientSignals = () => {
  if (_clientSignals) return _clientSignals;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const locale = (navigator.language || '').slice(0, 32);
    const region = (locale.split('-')[1] || '').toUpperCase();
    _clientSignals = { tz, locale, region: /^[A-Z]{2}$/.test(region) ? region : '' };
  } catch (_e) {
    _clientSignals = { tz: '', locale: '', region: '' };
  }
  return _clientSignals;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ss_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const sig = clientSignals();
  if (sig.tz) config.headers['X-Client-Timezone'] = sig.tz;
  if (sig.locale) config.headers['X-Client-Locale'] = sig.locale;
  if (sig.region) config.headers['X-Client-Region'] = sig.region;
  config.headers['X-Client-Platform'] = 'web';
  // V14.5 Part 17m — Fix all file/image uploads in the LMS.
  //
  // The instance default sets `Content-Type: application/json` on every
  // request. For FormData uploads (course thumbnails, profile photos,
  // banners, medical vault, baby tracker photos, etc.) this is FATAL:
  //
  //   - axios sticks `application/json` onto a multipart body
  //   - the browser never gets the chance to auto-generate the
  //     `multipart/form-data; boundary=<uuid>` header
  //   - FastAPI's multipart parser can't find the boundary
  //   - the `file: UploadFile = File(...)` parameter looks "missing"
  //   - response: 422 Unprocessable Content
  //
  // The defensive fix: when the request data is a FormData (or some
  // sites that pre-set `multipart/form-data` WITHOUT the required
  // boundary suffix), DELETE the Content-Type header so axios falls
  // back to its built-in FormData detection, which correctly delegates
  // to the browser to set the full Content-Type WITH the boundary.
  try {
    const data = config.data;
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    const explicitMultipart = typeof config.headers?.['Content-Type'] === 'string'
      && config.headers['Content-Type'].toLowerCase().startsWith('multipart/form-data');
    if (isFormData || explicitMultipart) {
      // Both header maps (axios merges defaults + request-level) must be cleared.
      if (config.headers && typeof config.headers === 'object') {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
  } catch (_e) {
    // never let interceptor errors break the request chain
  }
  // A pricing catalog response carries a ONE-TIME quote_id. Never let the browser
  // replay a cached (already consumed) quote on a payment retry.
  try {
    if ((config.url || '').includes('/api/pricing/catalog')) {
      config.params = { ...(config.params || {}), _ts: Date.now() };
      config.headers['Cache-Control'] = 'no-cache';
    }
  } catch (_e) { /* noop */ }
  return config;
});

// Handle 401 responses + flag missing /api prefix early (so calls don't
// silently 200 with HTML and break downstream JSON.parse).
api.interceptors.response.use(
  (response) => {
    // If the response was supposed to be JSON but is HTML, the URL almost
    // certainly forgot the `/api` prefix and the request hit the SPA.
    const ct = (response.headers?.['content-type'] || '').toLowerCase();
    const url = (response.config?.url || '');
    if (url.startsWith('/') && !url.startsWith('/api/') && ct.includes('text/html')) {
      console.error(`[api] Likely missing /api prefix on ${url} — response was HTML, expected JSON.`);
    }
    return response;
  },
  (error) => {
    // V14.5 Part 17k — Normalize FastAPI structured-error detail.
    //
    // FastAPI handlers commonly raise `HTTPException(status_code=..., detail={
    //   "code": "FOO", "message": "Human-readable"
    // })`. ~30+ downstream callers do `toast.error(err.response.data.detail)`
    // or `setError(err.response.data.detail)` — passing an object to a place
    // that expects a string. Sonner / direct JSX rendering then chokes with
    // React error #31 ("object with keys {code, message}") → the global
    // ErrorBoundary catches → "Something went wrong" page (production bug:
    // Admin → Users → Add Course → 400 → whole page crashes).
    //
    // Single fix: flatten `detail` to a friendly string here, while preserving
    // the original shape under `detailObject` for the (few) sites that
    // legitimately want the structured `code`.
    try {
      const d = error?.response?.data?.detail;
      if (d && typeof d === 'object' && !Array.isArray(d)) {
        const friendly = d.message || d.error || d.detail || JSON.stringify(d);
        error.response.data.detailObject = d;     // keep original for code-aware UIs
        error.response.data.detail = friendly;
      } else if (Array.isArray(d)) {
        // Pydantic validation errors come back as `[{loc, msg, type}, ...]`.
        const msgs = d.map(item => (
          typeof item === 'string' ? item :
          (item?.msg || item?.message || JSON.stringify(item))
        )).filter(Boolean);
        error.response.data.detailObject = d;
        error.response.data.detail = msgs.length ? msgs.join('; ') : 'Validation failed';
      }
    } catch (_normalizeErr) {
      // never let normalization itself break the interceptor chain
    }

    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
      const onAuthPage = ['/login', '/reset-password', '/refer/'].some(p => path.startsWith(p));
      // Public pages must NEVER bounce a visitor to the login screen — a class
      // join link has to work logged out, logged in, or with a stale token.
      const onPublicPage = ['/join/', '/host/', '/sso'].some(p => path.startsWith(p));
      const isAuthEndpoint = url.includes('/api/auth/');
      // Don't redirect when:
      //   - User is on the login/reset-password page already (they'll see the form's own toast)
      //   - User is on a public join/host page (no login required there)
      //   - The 401 came from /api/auth/* (this is an EXPECTED auth failure, not session expiry)
      if (!onAuthPage && !onPublicPage && !isAuthEndpoint) {
        localStorage.removeItem('ss_token');
        localStorage.removeItem('ss_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
